import { useState } from 'react';
import {
  getActiveCups,
  getUpcomingCups,
  getLatestChampions,
  getTimeRemainingText,
  formatCupDateRange,
  cupPeriodLabels,
  cupPeriodIcons,
  cupPeriodColors,
  cupPeriodBgColors,
  cupCategoryLabels,
  cupCategoryIcons,
  cupCategoryBadgeColors,
  cupGlobalStats,
  type RfwCup,
  type CupStanding,
} from '@/mocks/rfwCups';

const positionEmojis: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

function MiniStandingRow({ standing }: { standing: CupStanding }) {
  const isTop3 = standing.position <= 3;
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isTop3 ? 'bg-background-100/80' : 'hover:bg-background-100/50'}`}>
      <span className={`w-6 text-center font-bold text-xs ${standing.position <= 3 ? 'text-foreground-950' : 'text-foreground-400'}`}>
        {standing.position <= 3 ? positionEmojis[standing.position] : `#${standing.position}`}
      </span>
      <div className="w-7 h-7 rounded-full overflow-hidden bg-background-100 shrink-0 border border-background-200/70">
        <img src={standing.participantLogo} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <span className="text-xs font-semibold text-foreground-800 truncate flex-1 min-w-0">{standing.participantName}</span>
      <span className="text-xs font-bold text-foreground-950 shrink-0">{standing.points.toLocaleString()}</span>
      <span className={`text-[10px] shrink-0 w-4 text-center ${standing.trend === 'up' ? 'text-emerald-600' : standing.trend === 'down' ? 'text-red-500' : 'text-foreground-300'}`}>
        {standing.trend === 'up' ? '▲' : standing.trend === 'down' ? '▼' : '▬'}
      </span>
    </div>
  );
}

function CupCard({ cup }: { cup: RfwCup }) {
  const isActive = cup.status === 'active';
  const timeText = isActive ? getTimeRemainingText(cup.endDate) : `Inicia ${formatCupDateRange(cup.startDate, cup.endDate)}`;
  const top3 = cup.standings.slice(0, 3);

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-accent-200/60 transition-all duration-300 group">
      {/* Header */}
      <div className={`px-4 py-3 bg-gradient-to-r ${cupPeriodBgColors[cup.period]} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center`}>
              <i className={`${cupCategoryIcons[cup.category]} text-sm text-white`}></i>
            </span>
            <div>
              <p className="text-white font-bold text-sm">{cup.name}</p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-white/80 flex items-center gap-1">
                  <i className={`${cupPeriodIcons[cup.period]} text-[9px]`}></i>
                  {cupPeriodLabels[cup.period]}
                </span>
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-white/20 text-white'
                }`}>
                  {cupCategoryLabels[cup.category]}
                </span>
              </div>
            </div>
          </div>
          <div className="shrink-0 text-right">
            {isActive ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 text-white text-[10px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                {timeText}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 text-white text-[10px] font-bold">
                <i className="ri-calendar-line text-[10px]"></i>
                Próximamente
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Standings Preview */}
      {top3.length > 0 && (
        <div className="p-3 space-y-1">
          {top3.map((s) => (
            <MiniStandingRow key={s.participantId} standing={s} />
          ))}
        </div>
      )}

      {cup.standings.length === 0 && (
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-trophy-line text-xl text-foreground-300"></i>
          </div>
          <p className="text-xs text-foreground-500">Clasificación próximamente</p>
          <p className="text-[11px] text-foreground-400 mt-1">{cup.totalParticipants} participantes</p>
        </div>
      )}

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-background-200/70 flex items-center justify-between">
        <span className="text-[10px] text-foreground-400">
          {cup.standings.length > 0 ? `${cup.standings.length} de ${cup.totalParticipants} clasificados` : `${cup.totalParticipants} participantes`}
        </span>
        {cup.elimination && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 font-medium">
            Eliminatoria ×{cup.elimination.totalParticipants}
          </span>
        )}
      </div>
    </div>
  );
}

function ChampionCard({ entry }: { entry: { cupName: string; period: string; category: string; champion: CupStanding; date: string } }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-amber-300/50 transition-colors duration-200 cursor-pointer group">
      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 ring-2 ring-amber-200">
        <img src={entry.champion.participantLogo} alt={entry.champion.participantName} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-foreground-950 truncate">{entry.champion.participantName}</span>
          <span className="text-base shrink-0">🥇</span>
        </div>
        <p className="text-[10px] text-foreground-500 truncate">{entry.cupName}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[9px] px-1 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium">{entry.champion.points.toLocaleString()} pts</span>
          <span className="text-[9px] text-foreground-400">{new Date(entry.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
        </div>
      </div>
      <i className="ri-arrow-right-s-line text-foreground-300 text-sm shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"></i>
    </div>
  );
}

export default function CopasSection() {
  const [activeTab, setActiveTab] = useState<'active' | 'upcoming' | 'champions'>('active');

  const active = getActiveCups();
  const upcoming = getUpcomingCups();
  const champions = getLatestChampions(6);

  const tabs = [
    { id: 'active' as const, label: 'Campeonatos Activos', icon: 'ri-fire-line', count: active.length },
    { id: 'upcoming' as const, label: 'Próximas Copas', icon: 'ri-calendar-line', count: upcoming.length },
    { id: 'champions' as const, label: 'Últimos Campeones', icon: 'ri-trophy-line', count: champions.length },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
            COPAS RFW Y CAMPEONATOS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            La Competencia <span className="text-accent-600">Nunca Termina</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Copas Semanales, Mensuales y Anuales en 5 categorías. Cada semana hay un nuevo campeón. Cada mes, una nueva corona. Cada año, la gloria eterna. Las Copas RFW son el motor de actividad de toda la plataforma.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { label: 'Copas Activas', value: cupGlobalStats.activeCups.toString(), icon: 'ri-fire-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Participantes Ahora', value: cupGlobalStats.activeParticipantsNow.toString(), icon: 'ri-group-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Campeones Históricos', value: cupGlobalStats.totalChampions.toString(), icon: 'ri-trophy-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Puntos Otorgados', value: `${(cupGlobalStats.pointsAwarded / 1000).toFixed(0)}K`, icon: 'ri-flashlight-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-1 mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-accent-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'active' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {active.map((cup) => (
              <CupCard key={cup.id} cup={cup} />
            ))}
          </div>
        )}

        {activeTab === 'upcoming' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {upcoming.map((cup) => (
              <CupCard key={cup.id} cup={cup} />
            ))}
          </div>
        )}

        {activeTab === 'champions' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {champions.map((entry) => (
              <ChampionCard key={`${entry.cupId}-${entry.champion.participantId}`} entry={entry} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="/copas"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-trophy-line"></i>
            Ver Todas las Copas y Campeonatos
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-xs text-foreground-500 mt-3">
            Explora el Hall of Fame, los récords históricos y las eliminatorias en vivo
          </p>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-accent-100/70 border border-accent-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-fire-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">Las Copas RFW deben convertirse en uno de los principales motores de actividad de toda la plataforma.</p>
          <p className="text-xs text-foreground-600">Competí cada semana. Ganá cada mes. Hacé historia cada año.</p>
        </div>
      </div>
    </section>
  );
}